import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import styles from "./CrsMain.module.css";
import axios from 'axios';
import { API_URL } from "../../utils/auth";
import Auth from '../../utils/auth';

const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff', 
      '& + .MuiSwitch-track': {
        backgroundColor: 'rgba(87, 42, 241, 1)', 
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#fff', 
      border: '6px solid rgba(87, 42, 241, 0.3)', 
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
    backgroundColor: '#fff', 
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === 'dark' ? '#39393D' : '#E9E9EA',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

const CrsMain = () => {
  const [activeContent, setActiveContent] = useState("Onboarding Metrics");
  const [switches, setSwitches] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [circleConnected, setCircleConnected] = useState(false);
  const [isCircleToggleLoading, setIsCircleToggleLoading] = useState(true);
  const [circleToggleEnabled, setCircleToggleEnabled] = useState(false);

  const slugToSwitchMap = {
    'contract_signed': 'contractSigned',
    'welcome_call': 'welcomeCall',
    'onboarding_survey': 'onboardingSurvey',
    'onboarding_call': 'onboardCallCompleted',
    'group_access': 'groupAccess',
  };

  const switchToSlugMap = Object.fromEntries(
    Object.entries(slugToSwitchMap).map(([k, v]) => [v, k])
  );

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const workspaceId = Auth.getWorkspaceId();
        const user = Auth.getUser();
        const adminUserId = user?.id;
        
        if (!workspaceId) {
          throw new Error('Workspace ID not found');
        }

        // Fetch onboarding settings
        const response = await axios.get(`${API_URL}/admin/onboarding-settings`, {
          params: { workspaceId }
        });

        // Fetch Circle CRS settings separately
        await loadCircleCRSSetting();

        // Check Circle.so connection status
        if (workspaceId && adminUserId) {
          try {
            const circleResponse = await axios.get(
              `${API_URL}/circle/circle-token?workspaceId=${workspaceId}&adminUserId=${adminUserId}`
            );
            setCircleConnected(circleResponse.data.hasBotToken || false);
          } catch (err) {
            console.error("Failed to fetch Circle.so integration status:", err);
            setCircleConnected(false);
          }
        }

        if (response.data) {
          const initialSwitches = {
            contractSigned: false,
            welcomeCall: false,
            onboardingSurvey: false,
            onboardCallCompleted: false,
            groupAccess: false,
            aiCallScoring: false,
            contactFrequency: false,
            circleIntegration: false,
            npsSurvey: false,
            cesSurvey: false
          };

          const serverSwitches = response.data.reduce((acc, setting) => {
            const switchKey = slugToSwitchMap[setting.slug];
            if (switchKey) {
              acc[switchKey] = setting.enabled;
            }
            return acc;
          }, {});

          setSwitches({
            ...initialSwitches,
            ...serverSwitches
          });
        }
      } catch (err) {
        console.error('Error fetching onboarding settings:', err);
        setError('Failed to load settings. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const loadCircleCRSSetting = async () => {
    try {
      setIsCircleToggleLoading(true);
      const workspaceId = Auth.getWorkspaceId();
      if (!workspaceId) {
        console.error('Workspace ID not found');
        setIsCircleToggleLoading(false);
        return;
      }

      const response = await axios.get(`${API_URL}/admin/crs-settings`, {
        params: { workspaceId }
      });

      if (response.data && response.data.length > 0) {
        const circleIntegrationSetting = response.data.find(setting => setting.slug === 'circle');
        setCircleToggleEnabled(circleIntegrationSetting ? circleIntegrationSetting.enabled : false);
      } else {
        setCircleToggleEnabled(false);
      }
    } catch (error) {
      console.error('Error loading Circle CRS setting:', error);
      setCircleToggleEnabled(false);
    } finally {
      setIsCircleToggleLoading(false);
    }
  };

  const handleCircleCRSToggle = async (event) => {
    const { checked } = event.target;
    const workspaceId = Auth.getWorkspaceId();
    
    if (!workspaceId) {
      console.error('Workspace ID is missing!');
      return;
    }

    if (!circleConnected) {
      console.warn('Circle.so integration is not connected');
      return;
    }

    try {
      setCircleToggleEnabled(checked);

      const putData = {
        workspaceId: workspaceId,
        slug: 'circle',
        enabled: checked
      };

      await axios.put(`${API_URL}/admin/crs-settings`, putData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

    } catch (error) {
      console.error('Error updating Circle CRS setting:', error);
      setCircleToggleEnabled(!checked);
      setError('Failed to update Circle CRS setting. Please try again.');
    }
  };

  const handleSwitchChange = async (event) => {
    const { name, checked } = event.target;
    const workspaceId = Auth.getWorkspaceId();
    
    if (!workspaceId) {
      console.error('Workspace ID is missing!');
      return;
    }

    try {
      setSwitches(prev => ({
        ...prev,
        [name]: checked
      }));

      if (switchToSlugMap[name]) {
        const putData = {
          workspaceId: workspaceId,
          slug: switchToSlugMap[name],
          enabled: checked
        };

        await axios.put(`${API_URL}/admin/onboarding-settings`, putData, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
      }

    } catch (error) {
      console.error('Error updating setting:', error);
      setSwitches(prev => ({
        ...prev,
        [name]: !checked
      }));
      setError('Failed to update setting. Please try again.');
    }
  };

  const handleLinkClick = (e, content) => {
    e.preventDefault();
    setActiveContent(content);
  };

  const renderContent = () => {
    if (isLoading) {
      return <div className={styles.loading}>Loading settings...</div>;
    }

    if (error) {
      return <div className={styles.error}>{error}</div>;
    }

    if (!switches) {
      return <div className={styles.error}>No settings data available</div>;
    }

    switch (activeContent) {
      case "Onboarding Metrics":
        return (
          <div>
            <h2 className={styles.centeredHeading}>Onboarding Metrics</h2>
            <p>Toggle specific onboarding stages based on your client journey. Only active toggles will factor into CRS calculations, ensuring scores accurately reflect your onboarding process.</p>
            <p>Modify these settings anytime your onboarding workflow changes.</p>
            <div className={styles.iosSwitchContainer}>
              <IOSSwitch
                checked={switches.contractSigned}
                onChange={handleSwitchChange}
                name="contractSigned"
              />
              <span>Contract Signed</span>
            </div>
            <div className={styles.iosSwitchContainer}>
              <IOSSwitch
                checked={switches.welcomeCall}
                onChange={handleSwitchChange}
                name="welcomeCall"
              />
              <span>Welcome Call</span>
            </div>
            <div className={styles.iosSwitchContainer}>
              <IOSSwitch
                checked={switches.onboardingSurvey}
                onChange={handleSwitchChange}
                name="onboardingSurvey"
              />
              <span>Onboarding Survey</span>
            </div>
            <div className={styles.iosSwitchContainer}>
              <IOSSwitch
                checked={switches.onboardCallCompleted}
                onChange={handleSwitchChange}
                name="onboardCallCompleted"
              />
              <span>Onboard Call Completed</span>
            </div>
            <div className={styles.iosSwitchContainer}>
              <IOSSwitch
                checked={switches.groupAccess}
                onChange={handleSwitchChange}
                name="groupAccess"
              />
              <span>Group Access</span>
            </div>
          </div>
        );
      case "AI Call Scoring":
        return (
          <div>
            <h2 className={styles.centeredHeading}>AI Call Scoring</h2>
            <p>When no client calls have been scored, this metric will not negatively impact the client's CRS.</p>
            <p>This ensures your CRS accurately reflects actual client relationship health rather than data availability. Once calls are scored, they will automatically factor into the CRS calculation.</p>
            <p>This feature is recommended to be left on, but if you don't ever intend to use the AI Call Scoring technology you can toggle it off here.</p>
            <div className={styles.iosSwitchContainer}>
              <IOSSwitch
                checked={switches.aiCallScoring}
                onChange={handleSwitchChange}
                name="aiCallScoring"
              />
              <span>AI Call Scoring</span>
            </div>
          </div>
        );
      case "Contact Frequency Monitoring":
        return (
          <div>
            <h2 className={styles.centeredHeading}>Contact Frequency Monitoring</h2>
            <p>ClientBloom automatically tracks how well you're maintaining your selected touchpoint frequency with each client.</p>
            <p>Based on your settings: Weekly, Bi-Weekly, or Monthly</p>
            <p>These are manually toggled individually for each client in their dashboard.</p>
            <ul>
              <li><strong>On Track:</strong> When you've contacted your client within the selected timeframe, this positively impacts their CRS (green status)</li>
              <li><strong>Slightly Overdue:</strong> When contact is overdue but less than 50% beyond your target frequency, this moderately impacts their CRS (yellow status)</li>
              <li><strong>Significantly Overdue:</strong> When contact is overdue by more than 50% of your target frequency, this negatively impacts their CRS (red status)</li>
            </ul>
            <p>Simply update the "Last Contacted Date" whenever you interact with your client to maintain a healthy CRS. This ensures consistent communication remains a key factor in your retention strategy.</p>
            <p>This feature is recommended to be left on, but if you don't intend to use Touch Point Frequency for monitoring client health, you can toggle it off here.</p>
            <div className={styles.iosSwitchContainer}>
              <IOSSwitch
                checked={switches.contactFrequency}
                onChange={handleSwitchChange}
                name="contactFrequency"
              />
              <span>Touch Point Frequency</span>
            </div>
          </div>
        );
      case "Circle.so Community Activity Integration":
        return (
          <div>
            <h2 className={styles.centeredHeading}>Circle.so Community Activity Integration</h2>
            <p>To enable this functionality:</p>
            <p>First you must configure the Circle.so integration in your admin panel:</p>
            <ol style={{ paddingLeft: '2rem', color: '#555', lineHeight: '1.7', fontWeight: 'normal' }}>
              <li>Go to Admin Panel → Integrations</li>
              <li>Find the Circle.so integration card</li>
              <li>Click "Connect Circle.so"</li>
              <li>Follow the steps to authenticate and configure your Circle.so account</li>
              <li>Verify that the integration is working correctly</li>
              <li>Return here: Once configured, you can enable the toggle below to include Circle.so data in CRS calculation</li>
            </ol>
            <p><strong>When enabled, this integration will:</strong></p>
            <ul>
              <li>Pull activity scores (1-10) directly from Circle.so for applicable clients</li>
              <li>Include Circle activity as a component in CRS calculations only for clients with activity scores of 1.0 or higher</li>
              <li>Automatically exclude this metric for clients who aren't Circle members or have no activity (score of 0)</li>
            </ul>
            <p><strong>Important Notes:</strong></p>
            <ul>
              <li>This is a global setting that applies to your entire account</li>
              <li>Clients without Circle activity won't be penalized - their CRS will simply be calculated using other available metrics</li>
              <li>You'll need a valid Circle.so account with API access to enable this feature</li>
              <li>Activity scores update automatically when CRS is recalculated</li>
            </ul>
            <div className={styles.iosSwitchContainer}>
              {isCircleToggleLoading ? (
                <div className={styles.loadingToggle}>
                  <div className={styles.miniSpinner}></div>
                  <span>Loading Circle CRS setting...</span>
                </div>
              ) : (
                <>
                  <IOSSwitch
                    checked={circleToggleEnabled}
                    onChange={handleCircleCRSToggle}
                    name="circleToggleEnabled"
                    disabled={!circleConnected}
                  />
                  <span>Circle Activity Scoring</span>
                  {!circleConnected && (
                    <span style={{ color: '#888', fontSize: '0.85rem', marginLeft: '10px' }}>
                      (Integration required)
                    </span>
                  )}
                </>
              )}
            </div>
            <p>Need a Circle Account? <a href="#" onClick={(e) => e.preventDefault()}>Go to Resources</a></p>
          </div>
        );
      case "Net Promoter Score (NPS) Survey":
        return (
          <div>
            <h2 className={styles.centeredHeading}>Net Promoter Score (NPS)</h2>
            <p>Net Promoter Score measures client loyalty and likelihood to recommend your services. It's best used:</p>
            <ul>
              <li>Quarterly or bi-annually to gauge overall relationship health</li>
              <li>When you want to identify promoters who might refer new business</li>
              <li>To get a broad measure of client satisfaction and loyalty</li>
            </ul>
            <p><strong>When enabled, this feature allows you to:</strong></p>
            <ul>
              <li>Send NPS surveys to individual clients or in bulk</li>
              <li>Track responses on a 1-10 scale within each client's dashboard</li>
              <li>Automatically include NPS scores in Client Retention Score calculations</li>
              <li>Review feedback trends across your entire client base</li>
            </ul>
            <p><strong>Client Score Impact</strong></p>
            <ul>
              <li>Clients who have responded to NPS surveys will have their scores included in their CRS calculation</li>
              <li>Clients who haven't received or responded to a survey won't be penalized - their CRS will be calculated using other available metrics</li>
              <li>Only the most recent NPS response for each client will be considered in their CRS</li>
            </ul>
            <div className={styles.iosSwitchContainer}>
              <IOSSwitch
                checked={switches.npsSurvey}
                onChange={handleSwitchChange}
                name="npsSurvey"
              />
              <span>NPS Scoring</span>
            </div>
          </div>
        );
      case "Client Effort Score (CES) Survey":
        return (
          <div>
            <h2 className={styles.centeredHeading}>Client Effort Score (CES)</h2>
            <p>Client Effort Score measures how easy it was for clients to accomplish something with your company. It's best used:</p>
            <ul>
              <li>Immediately after specific interactions or deliverables</li>
              <li>When you want to identify friction points in your processes</li>
              <li>To measure the effectiveness of your client experience design</li>
              <li>Following support resolutions, onboarding completion, or project milestones</li>
            </ul>
            <p><strong>When enabled, this feature allows you to:</strong></p>
            <ul>
              <li>Send CES surveys after specific client interactions</li>
              <li>Track client responses on a 1-7 scale within each client's dashboard</li>
              <li>Automatically include CES scores in Client Retention Score calculations</li>
              <li>Identify friction points in your client processes</li>
            </ul>
            <p><strong>Client Score Impact</strong></p>
            <ul>
              <li>Clients who have responded to CES surveys will have their scores included in their CRS calculation</li>
              <li>Clients without CES responses won't be penalized - their CRS will be calculated using other available metrics</li>
              <li>Only the most recent CES response for each client will be considered in their CRS</li>
            </ul>
            <div className={styles.iosSwitchContainer}>
              <IOSSwitch
                checked={switches.cesSurvey}
                onChange={handleSwitchChange}
                name="cesSurvey"
              />
              <span>CES Scoring</span>
            </div>
          </div>
        );
      default:
        return <div className={styles.emptyState}>Select a metric to view details</div>;
    }
  };

  return (
    <div className={styles.wrapperOuter}>
      <div className={styles.wrapper}>
        <div className={`${styles.container} ${styles.definition}`}>
          <h1>What is the Client Retention Score? (CRS)</h1>
          <p>
            The Client Retention Score (CRS) is the core intelligence behind ClientBloom, consolidating multiple data points into a single, easy-to-understand metric that predicts client retention probability. Think of it as a "health score" for each client relationship that helps you identify at-risk accounts before they churn.
          </p>
          <p>The CRS algorithm improves over time by:</p>
          <ul>
            <li>Learning from your specific client base patterns</li>
            <li>Adapting to your industry's unique retention indicators</li>
            <li>Refining predictions based on actual retention outcomes</li>
            <li>Incorporating new data sources as they become available</li>
          </ul>
        </div>

        <div className={`${styles.container} ${styles.interpretation}`}>
          <h1>How to Interpret Score Changes</h1>
          <p>The CRS provides valuable insights through its trend analysis:<br /></p>
          <ul>
              <li><strong>Sudden Drops:</strong> Require immediate investigation and outreach</li>
              <li><strong>Gradual Decline:</strong> Indicates developing issues needing proactive attention</li>
              <li><strong>Steady Improvement:</strong> Shows successful engagement and relationship building</li>
              <li><strong>Fluctuations:</strong> May indicate inconsistent experience or seasonal patterns</li>
          </ul>
        </div>
      </div>

      <div className={styles.bottomWrapper}>
        <div className={`${styles.container} ${styles.customize}`}>
          <h1>Customize CRS Calculation</h1>
          <p>
            Toggle specific data metrics on or off based on your business processes. <br />
            When a metric is disabled, missing data in that category won't negatively impact individual client scores or your overall company CRS average. <br />
            This ensures your retention insights remain relevant to how you actually work with clients.
          </p>
          <h2>Metrics</h2>
          <ul className={styles.metricList}>
            <li>
              <a 
                href="#" 
                onClick={(e) => handleLinkClick(e, "Onboarding Metrics")} 
                className={activeContent === "Onboarding Metrics" ? styles.active : ""}
              >
                Onboarding Metrics
              </a>
            </li>
            <li>
              <a 
                href="#" 
                aria-disabled="true"
                className={styles.disabledLink}
                onClick={e => e.preventDefault()}
              >
                AI Call Scoring
              </a>
            </li>
            <li>
              <a 
                href="#" 
                aria-disabled="true"
                className={styles.disabledLink}
                onClick={e => e.preventDefault()}
              >
                Contact Frequency Monitoring
              </a>
            </li>
            <li>
              <a 
                href="#" 
                onClick={(e) => handleLinkClick(e, "Circle.so Community Activity Integration")} 
                className={activeContent === "Circle.so Community Activity Integration" ? styles.active : ""}
              >
                Circle.so Community Activity Integration
              </a>
            </li>
            <li>
              <a 
                href="#" 
                aria-disabled="true"
                className={styles.disabledLink}
                onClick={e => e.preventDefault()}
              >
                Net Promoter Score (NPS) Survey
              </a>
            </li>
            <li>
              <a 
                href="#" 
                aria-disabled="true"
                className={styles.disabledLink}
                onClick={e => e.preventDefault()}
              >
                Client Effort Score (CES) Survey
              </a>
            </li>
          </ul>
        </div>

        <div className={`${styles.container} ${styles.content}`}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default CrsMain;